import { useState, type FormEvent } from 'react';
import { useLogin } from '@/lib/session';

/**
 * Écran de connexion — plan §5.1 : username + mot de passe, gros champs, rien d'autre.
 * Dimensionné pour être utilisé debout, sur iPad, avec les doigts.
 *
 * Le damier bleu occupe tout l'écran ; le formulaire vit dans une carte opaque
 * pour que le contraste du texte reste garanti quel que soit le motif derrière.
 */
export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const login = useLogin();

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!username.trim() || !password) return;
    login.mutate({ username: username.trim(), password });
  }

  return (
    <main
      className="flex min-h-full items-center justify-center bg-[var(--brand-blue-dark)] bg-cover bg-center p-6"
      style={{ backgroundImage: 'url(/bg-super-show.jpg)' }}
    >
      <div className="w-full max-w-md space-y-6">
        <img
          src="/logo-super.png"
          alt="Super"
          className="mx-auto h-24 w-auto drop-shadow-lg"
          // Le logo est le premier élément visible : on le charge sans attendre.
          fetchPriority="high"
        />

        <form onSubmit={onSubmit} className="bg-card space-y-6 rounded-2xl p-6 shadow-2xl">
          <header className="text-center">
            <h1 className="text-2xl font-bold tracking-tight">Super Show</h1>
            <p className="text-muted-foreground mt-1 text-sm">Prise de commande</p>
          </header>

          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="username" className="text-sm font-medium">
                Nom d'utilisateur
              </label>
              <input
                id="username"
                name="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="username"
                spellCheck={false}
                required
                className="border-input bg-background focus:ring-ring h-16 w-full rounded-lg border px-4 text-xl focus:ring-2 focus:outline-none"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                Mot de passe
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="border-input bg-background focus:ring-ring h-16 w-full rounded-lg border px-4 text-xl focus:ring-2 focus:outline-none"
              />
            </div>
          </div>

          {login.isError && (
            <p role="alert" className="text-destructive text-center text-sm font-medium">
              {(login.error as Error).message}
            </p>
          )}

          <button
            type="submit"
            disabled={login.isPending}
            className="bg-primary text-primary-foreground h-16 w-full rounded-lg text-xl font-semibold disabled:opacity-50"
          >
            {login.isPending ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
      </div>
    </main>
  );
}
